import { getDefaultBoardBase, getRoomPrice } from './apiHelpers';

/**
 * Výběr nabídky pro hosta z Googlu a pomocné funkce pro microdata, která čte crawler Googlu.
 * https://developers.google.com/hotels/hotel-prices/structured-data/generic-crawler-guide
 */

/**
 * Přičítané poplatky z pobytu hrazené ve formuláři, za celý pobyt.
 *
 * @param {Object} fees pricelistFees
 * @param {Object} guests počty hostů podle kategorie
 * @param {Object} context currency a noOfNights
 */
function getFeesTotal(fees, guests, { currency, noOfNights }) {
	if (!fees) {
		return 0;
	}

	let total = 0;

	for (const feeKey of Object.keys(fees)) {
		const fee = fees[feeKey];

		if (fee.onlineForm !== 1) {
			continue;
		}

		for (const guestTypeId of Object.keys(guests)) {
			if (!guests[guestTypeId]) {
				continue;
			}

			if (fee.guestTypes && !fee.guestTypes.includes(parseInt(guestTypeId, 10))) {
				continue;
			}

			const feePrice = fee.price && fee.price[currency.id] && fee.price[currency.id][guestTypeId];
			if (feePrice) {
				total += feePrice * guests[guestTypeId] * noOfNights;
			}
		}
	}

	return total;
}

/**
 * Nabídka pokoje pro počet hostů z URL, nebo null, když se hosté do pokoje nevejdou.
 * Strava je context.boardBaseId, jinak výchozí.
 *
 * @param {Object} roomOffer
 * @param {Object} context viz getOfferContext()
 * @returns {{boardBaseId: string|null, guests: number, price: number}|null}
 */
function getOffer(roomOffer, context) {
	const { currency, guests, guestTypes, noOfNights, boardBases, extraFees, pricelistFees } = context;

	if (!roomOffer || !currency) {
		return null;
	}

	const offerGuests = {};
	Object.values(guestTypes || {}).forEach(guestType => {
		offerGuests[guestType.id] = guestType.default ? guests : 0;
	});

	const roomPrice = getRoomPrice(roomOffer, currency, offerGuests, guestTypes, null, extraFees || null, null, noOfNights);

	if (!roomPrice) {
		return null;
	}

	const boardBaseId = context.boardBaseId || (boardBases ? getDefaultBoardBase(boardBases) : null);
	const boardBase = boardBaseId && boardBases ? boardBases[boardBaseId] || null : null;

	// Cena stravy za osobu a noc, stejně jako ji počítá GoogleBookingLinksModule
	const boardBasePrice = boardBase && !boardBase.priceIncluded && boardBase.price
		? (boardBase.price[currency.id] || 0) * guests * noOfNights
		: 0;

	// Poplatky se uplatňují jen u typů pokojů, které je mají povolené
	const feesPrice = roomOffer.feesEnabled ? getFeesTotal(pricelistFees, offerGuests, context) : 0;

	return {
		boardBaseId: boardBase ? String(boardBase.id) : null,
		guests,
		price: roomPrice + boardBasePrice + feesPrice,
	};
}

/**
 * Pokoj a strava, které host vybral v Googlu (parametry room a ratePlan v URL). Bez parametru room
 * nejlevnější volná nabídka, protože Google ukazuje nejnižší cenu. Když pokoj z URL nebo žádný
 * pokoj není volný, vrací soldOut.
 *
 * @param {Array} roomOffers
 * @param {Object} context viz getOfferContext()
 * @param {string|null} room ID typu pokoje
 * @param {string|null} ratePlan "<strava>-<měna>", viz GoogleBookingLinksModule::getRatePlanCode()
 * @returns {{roomOffer: Object, offer: Object}|{soldOut: true}}
 */
export function getGoogleOffer(roomOffers, context, room, ratePlan) {
	const { boardBases, noOfNights } = context;
	const isAvailable = roomOffer => roomOffer.noOfVacant > 0 && noOfNights >= (roomOffer.minNights || 0);

	if (room) {
		const roomOffer = (roomOffers || []).find(roomOffer => String(roomOffer.id) === String(room));
		const ratePlanMatch = /^(\d+)-[A-Z]{3}$/.exec(String(ratePlan || ''));
		const boardBaseId = ratePlanMatch && boardBases && boardBases[ratePlanMatch[1]] ? ratePlanMatch[1] : null;
		const offer = roomOffer && isAvailable(roomOffer) ? getOffer(roomOffer, { ...context, boardBaseId }) : null;

		return offer ? { roomOffer, offer } : { soldOut: true };
	}

	const boardBaseIds = boardBases && Object.keys(boardBases).length ? Object.keys(boardBases) : [null];

	let cheapest = null;
	for (const roomOffer of (roomOffers || []).filter(isAvailable)) {
		for (const boardBaseId of boardBaseIds) {
			const offer = getOffer(roomOffer, { ...context, boardBaseId });

			if (offer && (!cheapest || offer.price < cheapest.offer.price)) {
				cheapest = { roomOffer, offer };
			}
		}
	}

	return cheapest || { soldOut: true };
}

/**
 * Kontext pro výpočet nabídek ze stavu aplikace.
 */
export function getOfferContext(state) {
	const formValues = state.form.bookingForm && state.form.bookingForm.values;
	const apiRoomsResponse = state.app.apiRoomsResponse || {};

	return {
		currency: formValues && formValues.currency,
		guests: state.app.guests || 1,
		guestTypes: state.app.guestTypes,
		noOfNights: state.app.noOfNights,
		boardBases: state.app.boardBases,
		extraFees: apiRoomsResponse.extraFees || null,
		pricelistFees: apiRoomsResponse.pricelistFees || {},
	};
}

/**
 * Převod data z formátu "5.2.2026" na "2026-02-05".
 */
export function formatDateToYMD(dateStr) {
	if (!dateStr) {
		return '';
	}

	const parts = dateStr.split('.');

	if (parts.length === 3) {
		return parts[2] + '-' + parts[1].padStart(2, '0') + '-' + parts[0].padStart(2, '0');
	}

	return dateStr;
}



// WEBPACK FOOTER //
// ./src/structuredData.js